import dayjs from "dayjs";
import { RootCauseState } from "../../../context/FiltersProvider/types";
import { DataQueryPayload } from "../../../datasources/api-client/data-contracts";
import { UserPeriodFilter } from "../../../state/company";
import { computeFiscalYears } from "../../fiscalPeriods";

const createDefaultPeriodFilter = (id: "baseline" | "comparison"): UserPeriodFilter => {
	const fiscalYears = computeFiscalYears("01-01", "12-31");
	const selectedPeriodId = id === "baseline" ? 0 : 1;

	return {
		selectedPeriodId,
		start: fiscalYears[selectedPeriodId].start,
		end: fiscalYears[selectedPeriodId].end,
		fiscalYears,
		type: "fiscal",
	};
};

const createEmptyDataQueryPayload = (): DataQueryPayload => ({
	operator: "and",
	filters: [],
	from: "",
	to: "",
});

export const createInitialRootCauseState = (): RootCauseState => ({
	perimeter: [],

	baseline: {
		dashboardFilters: [],
		advancedMode: false,
		advancedForm: [],
		appliedFilters: {
			payload: {
				operator: "and",
				filters: [],
				from: "",
				to: "",
			},
			lastAppliedAt: null,
		},
	},

	comparison: {
		dashboardFilters: [],
		advancedMode: false,
		advancedForm: [],
		appliedFilters: {
			payload: {
				operator: "and",
				filters: [],
				from: "",
				to: "",
			},
			lastAppliedAt: null,
		},
	},

	periods: {
		baseline: createDefaultPeriodFilter("baseline"),
		comparison: createDefaultPeriodFilter("comparison"),
	},

	payload: {
		perimeter: [],
		baseline: createEmptyDataQueryPayload(),
		comparison: createEmptyDataQueryPayload(),
		perspective: "operational",
	},
});

const normalizeFiscalYears = (fiscalYears: UserPeriodFilter["fiscalYears"]) =>
	fiscalYears.map((year) => ({
		...year,
		start: dayjs(year.start),
		end: dayjs(year.end),
	}));

export const normalizeRootCauseState = (stored: RootCauseState | null): RootCauseState => {
	const initial = createInitialRootCauseState();

	if (!stored) return initial;

	return {
		...initial,
		...stored,

		periods: {
			baseline: {
				...initial.periods.baseline,
				...stored.periods?.baseline,
				start: dayjs(stored.periods?.baseline?.start ?? initial.periods.baseline.start),
				end: dayjs(stored.periods?.baseline?.end ?? initial.periods.baseline.end),
				fiscalYears: normalizeFiscalYears(
					stored.periods?.baseline?.fiscalYears ?? initial.periods.baseline.fiscalYears
				),
			},

			comparison: {
				...initial.periods.comparison,
				...stored.periods?.comparison,
				start: dayjs(stored.periods?.comparison?.start ?? initial.periods.comparison.start),
				end: dayjs(stored.periods?.comparison?.end ?? initial.periods.comparison.end),
				fiscalYears: normalizeFiscalYears(
					stored.periods?.comparison?.fiscalYears ?? initial.periods.comparison.fiscalYears
				),
			},
		},
	};
};
